import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { clientDisplayName, IAgencyAlert } from '@innerhive-internal/innerhive-common';
import { BRAND, CHART_INK, KIND_COLOR, KIND_LABEL } from './agencyChartTheme';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { PersonAvatar } from '../../avatar/Avatars';
import { OrganizationInsightsState } from '../OrganizationInsightsState';

const useStyles = createUseStyles({
    rows: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 2
    },

    row: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '13px 8px',
        background: 'none',
        border: 0,
        borderTop: `1px solid ${BRAND.tealEdge}`,
        borderRadius: 8,
        font: 'inherit',
        textAlign: 'left' as const,
        cursor: 'pointer',

        '&:first-child': {
            borderTop: 'none'
        },
        '&:hover': {
            background: BRAND.tealWash
        }
    },

    body: {
        // The three lines are spans, so without a column flow they share one line box and their
        // margins go inert: the name, the finding and the kind run together into one string.
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 3,
        flex: 1,
        minWidth: 0
    },

    name: {
        fontSize: 13.5,
        fontWeight: 500,
        color: BRAND.deepTeal
    },

    title: {
        fontSize: 13,
        lineHeight: 1.45,
        color: CHART_INK.strongLabel
    },

    kind: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: CHART_INK.label
    },

    dot: {
        flexShrink: 0,
        width: 8,
        height: 8,
        borderRadius: '50%'
    },

    quiet: {
        fontSize: 12.5,
        color: CHART_INK.label
    },

    more: {
        marginTop: 10,
        fontSize: 12.5,
        color: CHART_INK.label
    }
});

/**
 * The worklist: who has what outstanding, each row a way into that client's detail view.
 *
 * The dot takes the same colour the kind has everywhere else in the tab, so red always means
 * something already happened and coral always means a decline against how this person usually is.
 */
export const AttentionList: FC<{
    alerts: IAgencyAlert[];
    /** Findings that matched but did not fit, so a capped worklist cannot read as complete. */
    unlistedCount: number;
    /** Whether the rows were already narrowed, which changes what an empty list means. */
    narrowed: boolean;
    onSelectClient: (careMapId: string) => void;
}> = ({ alerts, unlistedCount, narrowed, onSelectClient }) => {
    const classes = useStyles();
    const insightsState = useInstance(OrganizationInsightsState);

    if (alerts.length === 0) {
        return (
            <div className={classes.quiet} data-id="insights-alerts-empty">
                {/* No unreadable caveat here on purpose: the Bea line rendered directly beneath
                    this list in the same card carries it, and the two empty states print one
                    after the other, where the same sentence twice reads as a rendering fault. */}
                {narrowed
                    ? 'Nothing in this selection.'
                    : 'Nothing outstanding across the caseload right now.'}
            </div>
        );
    }

    return (
        <>
            <div className={classes.rows}>
                {alerts.map((alert, index) => (
                    <button
                        key={`${alert.careMapId}-${index}`}
                        type="button"
                        className={classes.row}
                        data-id="insights-alert"
                        data-kind={alert.kind}
                        onClick={() => onSelectClient(alert.careMapId)}
                    >
                        <PersonAvatar size={28} identity={insightsState.getClientAvatar(alert.careMapId)} name={alert.clientName} />
                        <span className={classes.body}>
                            <span className={`${classes.name} mp-mask`}>{clientDisplayName(alert.clientName)}</span>
                            <span className={classes.title}>{alert.title}</span>
                            <span className={classes.kind}>
                                <span className={classes.dot} style={{ background: KIND_COLOR[alert.kind] }} />
                                {KIND_LABEL[alert.kind]}
                            </span>
                        </span>
                    </button>
                ))}
            </div>

            {unlistedCount > 0 && (
                <div className={classes.more} data-id="insights-alerts-more">
                    {unlistedCount} older {unlistedCount === 1 ? 'finding is' : 'findings are'} not shown.
                </div>
            )}
        </>
    );
};
